import React, { useState } from "react";
import KuikaDatePicker, { KuikaDatePickerMode } from "../../../../ui-kit/kuika-date-picker/kuika-date-picker";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { noop } from "../_layout/utils";
import { VariantGrid } from "../_layout/variant-grid";

type DatePickerSize = "sm" | "md";

interface IStatefulProps {
  mode?: KuikaDatePickerMode;
  initial?: string;
  size?: DatePickerSize;
  variant?: "standalone" | "cell";
  showClear?: boolean;
}

const Stateful: React.FC<IStatefulProps> = ({ mode = "date", initial = "2026-03-15", ...rest }) => {
  const [value, setValue] = useState<string>(initial);
  return (
    <KuikaDatePicker
      mode={mode}
      value={value}
      onChange={(next: string | null): void => setValue(next ?? "")}
      {...rest}
    />
  );
};

// A stand-in grid cell: the cell variant fills whatever box the table editor gives it.
const CELL_STYLE: React.CSSProperties = {
  display: "flex",
  width: 200,
  height: 32,
  border: "var(--border)",
  background: "var(--dark-0)"
};

export const DatePickerStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Date Picker"
      componentName="KuikaDatePicker"
      sourcePath="ui-kit/kuika-date-picker"
      description="Button trigger over a Radix popover with a react-day-picker calendar (plus a time row for datetime modes). Always DD/MM/YYYY, never the browser locale. Paging a month slides the day grid out and the next one in, in the paging direction."
    >
      <VariantGrid
        label="size"
        caption="standalone frame on the control ladder — sm 24 · md 32 (default)"
        cols={["sm", "md"]}
        cellMinWidth={220}
        render={(size: string): React.ReactNode => <Stateful size={size as DatePickerSize} />}
      />
      <VariantGrid
        label="state · size"
        caption="empty shows the placeholder; disabled dims to --disabled-opacity"
        rows={["empty", "disabled"]}
        cols={["sm", "md"]}
        cellMinWidth={220}
        render={(state: string, size: string): React.ReactNode => (
          <KuikaDatePicker
            mode="date"
            value={state === "empty" ? "" : "2026-03-15"}
            size={size as DatePickerSize}
            disabled={state === "disabled"}
            onChange={noop}
          />
        )}
      />
      <SpecRow label="Cell" caption="variant=cell size=sm — fills the table editor's cell, no frame of its own">
        <div style={CELL_STYLE}>
          <Stateful variant="cell" size="sm" />
        </div>
      </SpecRow>
      <SpecRow
        label="Month paging"
        caption="open, then page with ‹ ›: the grid leaves and the next month enters in the paging direction; the caption rolls with it. Reduced motion, or arrow keys crossing a month edge inside the grid, switch at once"
      >
        <Stateful />
      </SpecRow>
      <SpecRow label="Date and time" caption="mode=datetime showClear — time row and Clear / Done below the calendar">
        <Stateful mode="datetime" initial="2026-03-15T09:30:00" showClear />
      </SpecRow>
    </ShowcaseFrame>
  );
};
